<template>
    <div class="edit-modal-wrapper">
        <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary"></b-spinner>
        </div>
        <template v-else>
            <b-row>
                <b-col md="4">
                    <b-card no-body class="mb-3">
                        <b-card-header class="bg-light font-weight-bold">
                            {{ $t('application_information') }}
                        </b-card-header>
                        <b-list-group flush>
                            <b-list-group-item><strong>{{ $t('name_surname') }}:</strong> {{ detail.name && detail.name.toLocaleUpperCase() }} {{ detail.surname && detail.surname.toLocaleUpperCase() }}</b-list-group-item>
                            <b-list-group-item><strong>{{ $t('national_id') }}:</strong> {{ detail.national_id }}</b-list-group-item>
                            <b-list-group-item v-if="detail.passport_number"><strong>{{ $t('passport_number') }}:</strong> {{ detail.passport_number }}</b-list-group-item>
                            <b-list-group-item><strong>{{ $t('university') }}:</strong> {{ detail.last_school && detail.last_school.toLocaleUpperCase() }}</b-list-group-item>
                            <b-list-group-item>
                                <strong>{{ $t('status') }}:</strong>
                                <span :class="getProgramStatusClass(detail.application_status)" class="badge ml-2">
                                   {{ getLocaleText(detail, 'application_status_name') }}
                                </span>
                            </b-list-group-item>
                        </b-list-group>
                    </b-card>
                </b-col>

                <b-col md="8">
                    <b-tabs content-class="mt-3" card>
                        <b-tab :title="$t('document_approval')" active>
                            <div
                                v-for="(doc, index) in form.document_evaluations"
                                :key="index"
                                class="document-item border p-3 mb-2 rounded shadow-sm bg-white"
                            >
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <span class="font-weight-bold">{{ getDocumentTypeName(doc.document_type) }}</span>
                                    <b-button size="sm" variant="outline-primary" @click="viewFile(doc.id)">
                                        <i class="ri-eye-line"></i> {{ $t('view') }}
                                    </b-button>
                                </div>
                                <b-row>
                                    <b-col sm="6">
                                        <b-form-group :label="$t('status')">
                                            <ParameterSelectbox
                                                code="visitor_student_application_documents_status"
                                                v-model="doc.status"
                                                size="sm"
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col sm="6" v-if="doc.status === 'rejected'">
                                        <b-form-group :label="$t('rejection_reason')">
                                            <b-form-input
                                                size="sm"
                                                v-model="doc.description"
                                                :placeholder="$t('write_reason_for_rejection')"
                                            ></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                                <div class="text-right">
                                    <b-button
                                        size="sm"
                                        variant="primary"
                                        :disabled="saving['doc_' + doc.id] || !doc.status"
                                        @click="saveDocumentEvaluation(doc)"
                                    >
                                        <b-spinner small v-if="saving['doc_' + doc.id]"></b-spinner>
                                        {{ $t('save') }}
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>

                        <b-tab :title="$t('course_registration')">
                            <div
                                v-for="(course, index) in form.course_evaluations"
                                :key="index"
                                class="document-item border p-3 mb-2 rounded shadow-sm bg-white"
                            >
                                <div class="mb-1">
                                    <span class="font-weight-bold">{{ course.course_code }}</span>
                                    <span class="ml-2 text-muted">{{ course.course_name }}</span>
                                </div>
                                <div class="text-muted small mb-2">
                                    <span v-if="course.section">{{ $t('section') }}: {{ course.section }}</span>
                                    <span v-if="course.instructor_name" class="ml-3">{{ $t('instructor') }}: {{ course.instructor_name }}</span>
                                </div>
                                <b-row>
                                    <b-col sm="6">
                                        <b-form-group :label="$t('status')">
                                            <ParameterSelectbox
                                                code="visitor_student_application_program_status"
                                                v-model="course.status"
                                                size="sm"
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col sm="6" v-if="course.status === 'rejected'">
                                        <b-form-group :label="$t('rejection_reason')">
                                            <b-form-input
                                                size="sm"
                                                v-model="course.rejection_reason"
                                                :placeholder="$t('write_reason_for_rejection')"
                                            ></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                                <div class="text-right">
                                    <b-button
                                        size="sm"
                                        variant="primary"
                                        :disabled="saving['course_' + course.id] || !course.status"
                                        @click="saveCourseEvaluation(course)"
                                    >
                                        <b-spinner small v-if="saving['course_' + course.id]"></b-spinner>
                                        {{ $t('save') }}
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>

                        <b-tab :title="$t('program_information')">
                            <div class="program-cards-grid">
                                <div
                                    v-for="(prog, index) in form.program_evaluations"
                                    :key="index"
                                    class="program-card"
                                >
                                    <div class="program-card-icon">
                                        <i :class="getProgramLevelIcon(prog.program_level)"></i>
                                    </div>
                                    <div class="program-card-level">{{ getLocaleText(prog, 'program_level_name') }}</div>
                                    <div class="program-card-name">{{ prog.program_name }}</div>
                                    <div class="program-card-code text-muted small">{{ prog.program_code }}</div>
                                </div>
                            </div>
                        </b-tab>

                        <b-tab :title="$t('update_statuses')">
                            <b-row>
                                <b-col sm="12" md="4">
                                    <b-form-group :label="$t('application_status')">
                                        <ParameterSelectbox
                                            v-model="statusForm.application_status"
                                            code="visitor_student_application_statuses"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col sm="12" md="4">
                                    <b-form-group :label="$t('accepttance_status')">
                                        <ParameterSelectbox
                                            v-model="statusForm.acceptance_status"
                                            code="visitor_student_application_acceptance_statuses"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col sm="12" md="4">
                                    <b-form-group :label="$t('application_completion_status')">
                                        <ParameterSelectbox
                                            v-model="statusForm.completion_status"
                                            code="visitor_student_application_completion_statuses"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <div class="text-right mt-3">
                                <b-button
                                    variant="primary"
                                    size="sm"
                                    :disabled="saving.statuses || !hasAnyStatus"
                                    @click="saveStatuses"
                                >
                                    <b-spinner small v-if="saving.statuses"></b-spinner>
                                    {{ $t('save') }}
                                </b-button>
                            </div>
                        </b-tab>

                    </b-tabs>
                </b-col>
            </b-row>

            <hr>

            <div class="d-flex justify-content-end align-items-end p-2 gap-3">
                <b-form-group :label="$t('application_status')" class="mb-0" style="min-width: 220px;">
                    <ParameterSelectbox
                        code="visitor_student_application_statuses"
                        v-model="form.status"
                    />
                </b-form-group>
                <b-button variant="primary" :disabled="isSaving" @click="submitAll" class="mb-0 ml-2">
                    <b-spinner small v-if="isSaving"></b-spinner>
                    {{ $t('update') }}
                </b-button>
            </div>
        </template>
    </div>
</template>

<script>
import VisitorStudentApplicationService from "@/services/VisitorStudentApplicationServices";
import ParameterSelectbox from "@/components/interactive-fields/ParameterSelectbox.vue";

export default {
    name: "EditModal",
    components: { ParameterSelectbox },
    props: {
        applicationId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            isSaving: false,
            detail: {},
            form: {
                status: null,
                document_evaluations: [],
                course_evaluations: [],
                program_evaluations: []
            },
            statusForm: {
                application_status: null,
                acceptance_status: null,
                completion_status: null
            },
            saving: {}
        }
    },
    computed: {
        hasAnyStatus() {
            return !!(this.statusForm.application_status || this.statusForm.acceptance_status || this.statusForm.completion_status);
        }
    },
    mounted() {
        this.fetchDetail();
    },
    methods: {
        fetchDetail() {
            this.isLoading = true;
            VisitorStudentApplicationService.get(this.applicationId)
                .then(response => {
                    this.detail = response.data.data;
                    this.initForm(this.detail);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        initForm(data) {
            this.form.status = data.status ?? null;

            this.form.document_evaluations = (data.documents || []).map(doc => ({
                id: doc.id,
                document_type: doc.document_type,
                status: doc.status ?? 'uploaded',
                description: doc.description ?? ''
            }));

            this.form.course_evaluations = (data.courses || []).map(course => ({
                id: course.id,
                course_code: course.course_code,
                course_name: course.course_name,
                section: course.section,
                instructor_name: course.instructor_name,
                status: course.status ?? 'waiting',
                rejection_reason: course.rejection_reason ?? ''
            }));

            this.form.program_evaluations = (data.programs || []).map(prog => ({
                id: prog.id,
                program_name: data.program_name,
                program_code: prog.program_code,
                program_level_name: data.program_level_name,
                program_level_name_en: data.program_level_name_en,
                graduate_type: prog.graduate_type,
                status: data.program_status_name,
                rejection_reason: prog.rejection_reason ?? ''
            }));
        },
        getDocumentTypeName(type) {
            if (!type) return '-';
            const key = type.toLowerCase();
            const translated = this.$t(key);
            if (translated === key) {
                return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            }
            return translated;
        },
        viewFile(documentId) {
            this.$toast.info(`Belge görüntüleniyor (ID: ${documentId})`);
        },
        async saveDocumentEvaluation(doc) {
            if (!doc.status) return;
            if (doc.status === 'rejected' && !doc.description) {
                this.$toast.error(this.$t('rejection_reason') + ' ' + this.$t('is_required'));
                return;
            }
            this.$set(this.saving, 'doc_' + doc.id, true);
            try {
                const payload = { document_id: doc.id, status: doc.status };
                if (doc.status === 'rejected') payload.description = doc.description;
                await VisitorStudentApplicationService.evaluateDocument(this.applicationId, payload);
                this.$toast.success(this.$t('successfully_saved'));
            } catch (e) {
                this.$toast.error(e?.data?.message || this.$t('an_error_occurred'));
            } finally {
                this.$set(this.saving, 'doc_' + doc.id, false);
            }
        },
        async saveCourseEvaluation(course) {
            if (!course.status) return;
            if (course.status === 'rejected' && !course.rejection_reason) {
                this.$toast.error(this.$t('rejection_reason') + ' ' + this.$t('is_required'));
                return;
            }
            this.$set(this.saving, 'course_' + course.id, true);
            try {
                const payload = { course_id: course.id, status: course.status };
                if (course.status === 'rejected') payload.rejection_reason = course.rejection_reason;
                await VisitorStudentApplicationService.evaluateCourse(this.applicationId, payload);
                this.$toast.success(this.$t('successfully_saved'));
            } catch (e) {
                this.$toast.error(e?.data?.message || this.$t('an_error_occurred'));
            } finally {
                this.$set(this.saving, 'course_' + course.id, false);
            }
        },
        async saveStatuses() {
            if (!this.hasAnyStatus) return;
            this.$set(this.saving, 'statuses', true);
            try {
                const payload = {};
                if (this.statusForm.application_status) payload.application_status = this.statusForm.application_status;
                if (this.statusForm.acceptance_status) payload.acceptance_status = this.statusForm.acceptance_status;
                if (this.statusForm.completion_status) payload.completion_status = this.statusForm.completion_status;
                await VisitorStudentApplicationService.updateStatuses(this.applicationId, payload);
                this.$toast.success(this.$t('successfully_saved'));
            } catch (e) {
                this.$toast.error(e?.data?.message || this.$t('an_error_occurred'));
            } finally {
                this.$set(this.saving, 'statuses', false);
            }
        },
        getProgramLevelIcon(level) {
            const icons = {
                'L': 'ri-book-open-line',
                'YL': 'ri-medal-line',
                'D': 'ri-award-line'
            };
            return icons[level] || 'ri-school-line';
        },
        getProgramStatusClass(status) {
            const classes = {
                'waiting_approval': 'badge-primary',
                'accepted': 'badge-success',
                'rejected': 'badge-danger',
                'document_waiting': 'badge-secondary',
            };
            return classes[status] || 'badge-secondary';
        },
        submitAll() {
            this.isSaving = true;
            const payload = {
                status: this.form.status,
                document_evaluations: this.form.document_evaluations.map(d => ({
                    id: d.id,
                    status: d.status,
                    description: d.description
                })),
                course_evaluations: this.form.course_evaluations.map(c => ({
                    id: c.id,
                    status: c.status,
                    rejection_reason: c.rejection_reason
                })),
                program_evaluations: this.form.program_evaluations.map(p => ({
                    id: p.id,
                    status: p.status,
                    rejection_reason: p.rejection_reason
                }))
            };
            VisitorStudentApplicationService.evaluate(this.applicationId, payload)
                .then(() => {
                    this.$emit('saved');
                })
                .finally(() => {
                    this.isSaving = false;
                });
        }
    }
}
</script>

<style scoped>
.document-item {
    transition: all 0.2s ease-in-out;
}
.document-item:hover {
    background-color: #f8f9fa !important;
}

.program-cards-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding: 8px 0;
}

.program-card {
    flex: 0 0 calc(33.333% - 12px);
    min-width: 180px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px 16px 20px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.2s ease;
}

.program-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.program-card-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background-color: #eef2fb;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;
    font-size: 24px;
    color: #3d5a99;
}

.program-card-level {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #2d3748;
    margin-bottom: 4px;
}

.program-card-name {
    font-size: 12px;
    color: #4a5568;
    margin-bottom: 2px;
}

.program-card-code {
    font-size: 11px;
}

.program-card-status {
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 20px;
}
</style>
